export interface AddToCartItemInput {
    itemId: string; // product.manufacturerItem
    itemName: string; // product.productTitle
    price: number; // unit net price
    quantity: number; // qty actually added
    brand?: string; // product.brand?.name
    itemCategory?: string | string[];
    // breadCrumbs?: { text: string; url?: string }[];
    currency?: string;
    // coupon?: string;
    itemListId?: string;
    itemListName?: string;
}

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const formatItemListNameFromId = (itemListId: string) =>
    itemListId
        .split("_")
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

export type ItemListType =
    | "brand"
    | "category"
    | "search"
    | "wishlist_list_page"
    | "wishlist_Details_page"
    | "order_history_List"
    | "order_history_details"
    | "Saved_order"
    | "plp"
    | "pdp"
    | "compare"
    | "carousel"
    |"my_list_details";

const ITEM_LIST_LABELS: Record<ItemListType, string> = {
    brand: "Brand",
    category: "Category",
    search: "Search",
    wishlist_list_page: "Wishlist",
    wishlist_Details_page: "Wishlist Details Page",
    order_history_List: "Order History List",
    order_history_details: "Order History Details",
    Saved_order: "Saved Order Details",
    plp: "Product List",
    pdp: "Product Detail",
    compare: "Compare Products",
    carousel: "Carousel",
    my_list_details: "My List Details",
};

// Fixed places that don't need a name: id is the type, name is the label.
const STATIC_TYPES: ItemListType[] = ["plp", "pdp", "compare"];

/**
 * Builds GA4 item list fields.
 *   buildItemList("brand", "Strahman") -> { itemListId: "brand_strahman", itemListName: "Brand: Strahman" }
 *   buildItemList("pdp")               -> { itemListId: "pdp", itemListName: "Product Detail" }
 */
export const buildItemList = (
    type: ItemListType,
    name?: string | null,
): { itemListId?: string; itemListName?: string } => {
    const label = ITEM_LIST_LABELS[type];
    const cleanName = name?.trim();

    if (!cleanName) {
        return STATIC_TYPES.includes(type) ? { itemListId: type.toLowerCase(), itemListName: label } : {};
    }

    if (type === "search") {
        return {
            itemListId: "search_results",
            itemListName: `Search: ${cleanName}`,
        };
    }

    const itemListId = `${type}_${slugify(cleanName)}`.toLowerCase();
    const isCarouselList = type.toLowerCase().startsWith("carousel");
    const itemListName = isCarouselList ? formatItemListNameFromId(itemListId) : label ? `${label}: ${cleanName}` : cleanName;

    return {
        itemListId,
        itemListName,
    };
};

export const getCategoryProps = (
    breadCrumbs?: { text: string; url?: string }[] | null,
    options?: { excludeLast?: boolean },
) => {
    if (!breadCrumbs) return {};
    const crumbs = breadCrumbs
        .filter(b => b.text && b.text !== "Home")
        .slice(0, options?.excludeLast ? -1 : undefined)
        .slice(0, 5);

    return Object.fromEntries(
        crumbs.map((b, i) => [i === 0 ? "item_category" : `item_category${i + 1}`, b.text]),
    );
};

const getItemCategoryProps = (itemCategory?: string | string[] | null) => {
    if (!itemCategory) return {};

    const categories = Array.isArray(itemCategory) ? itemCategory.filter(Boolean) : [itemCategory];

    if (categories.length === 0) return {};

    return categories.reduce<Record<string, string>>((acc, category, index) => {
        acc[index === 0 ? "item_category" : `item_category${index + 1}`] = category;
        return acc;
    }, {});
};
export const buildCarouselItemList = (
    carouselType?: string | null,
    title?: string | null,
    fallbackLabel?: string | null,
): { itemListId: string; itemListName: string } => {
    const name = title?.trim() || fallbackLabel?.trim() || carouselType?.trim() || "Product Carousel";
    return {
        itemListId: `carousel_${slugify(name)}`,
        itemListName: `Carousel: ${name}`,
    };
};

const pushAddToCartEvent = (input: AddToCartItemInput | AddToCartItemInput[]) => {
    if (typeof window === "undefined") return;
    const inputs = Array.isArray(input) ? input : [input];
    if (inputs.length === 0) return;

    const w = window as any;
    w.dataLayer = w.dataLayer || [];

    w.dataLayer.push({ ecommerce: null });
    w.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
            currency: inputs[0].currency || "USD",
            value: inputs.reduce((sum, i) => sum + i.price * i.quantity, 0),
            items: inputs.map(i => ({
                item_id: i.itemId,
                item_name: i.itemName,
                price: i.price,
                quantity: i.quantity,
                item_brand: i.brand,
                ...getItemCategoryProps(i.itemCategory),
                // coupon: i.coupon ?? "",
                ...(i.itemListId
                    ? { item_list_id: i.itemListId, item_list_name: i.itemListName ?? "" }
                    : {}),
            })),
        },
    });
};

export default pushAddToCartEvent;